import React, { useLayoutEffect, useMemo, useRef } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import { fg } from '@atlaskit/platform-feature-flags/fg';
// Per-component subpath: `@atlaskit/primitives/compiled` is a deprecated re-export shim
// and new imports of it fail the deprecated-entry-point ratchet.
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { type TriggerProps } from '@atlaskit/tooltip/types';
import { isCodeSearchEnabled } from '@atlassian/search-common/code-search-mode';

import { isNounKey, type NounKeys } from '../../../../../common/constants/nouns';
import { is1pProductKey, type ProductKeys } from '../../../../../common/constants/product-keys';
import { ProductOrNounIcon } from '../../../../../common/ui/product-icon';
import { type AppOption } from '../../../../../common/utils/app-filter-options';
import { warmUpCodeSearchForHoveredProduct } from '../../../../../common/utils/code-search-warmup';
import { shouldShowRefreshedFilterRow } from '../../../../../common/utils/experiments';
import { isAtlassianOwned3pProduct } from '../../../../../common/utils/is-atlassian-owned-3p-product';
import { mergeRefs } from '../../../../../common/utils/merge-refs';
import { usePrimaryProduct } from '../../../../../controllers/store/bootstrap';
import { useSelectedProduct } from '../../../../../controllers/store/search';
import { useLiteFilterSelect } from '../../lite-app-filter-bar/use-lite-filter-select';
import { messages } from './messages';
import {
	animateLabelWidth,
	animateTileIn,
	animateTileOut,
	cancelTileAnimations,
	measureLabelWidth,
} from './tile-motion';
import { DEFAULT_MAX_VISIBLE_APPS, useRefreshAppOptions } from './use-refresh-app-options';
import { type PresentTile, useTilePresence } from './use-tile-presence';

type OnSelectApp = (value: ProductKeys | NounKeys | undefined) => void;

// The selected app swapped in from More apps keeps one key whichever app it holds, so
// choosing a second one from the dropdown relabels the same tile rather than replacing it.
const PROMOTED_SLOT_KEY = 'promoted-slot';

// Selecting an app is one gesture: the outgoing tile's label collapses while the incoming
// one's grows, over the same duration and curve, so the row's total width changes smoothly
// instead of narrowing and then widening. Colour changes are quicker feedback on top.
const styles = cssMap({
	// A single-column grid animating 0fr -> 1fr: its end point is the label's own width,
	// so every label takes the full duration to open, and closes as the exact reverse.
	// A fixed max-width end point would open a short label almost instantly and hold it
	// open for most of its exit.
	labelTrack: {
		display: 'inline-grid',
		gridTemplateColumns: '0fr',
		opacity: 0,
		marginInlineStart: token('space.0'),
		transitionProperty: 'grid-template-columns, opacity, margin-inline-start',
		transitionDuration: `${token('motion.duration.long')}, ${token('motion.duration.medium')}, ${token('motion.duration.long')}`,
		transitionTimingFunction: token('motion.easing.out.bold'),
		'@media (prefers-reduced-motion: reduce)': {
			transitionDuration: '0ms',
		},
	},
	labelTrackExpanded: {
		gridTemplateColumns: '1fr',
		opacity: 1,
		marginInlineStart: token('space.075'),
	},
	labelText: {
		overflow: 'hidden',
		minWidth: '0px',
		whiteSpace: 'nowrap',
	},
	// Matches Button's subtle/selected appearance and its 12px padding. The padding is the
	// same labelled or icon-only, so a label opening or closing never moves the icon; only
	// the label track changes width.
	tile: {
		display: 'inline-flex',
		alignItems: 'center',
		boxSizing: 'border-box',
		position: 'relative',
		flexShrink: 0,
		// Clips the icon and label while the tile grows out of, or collapses to, zero width.
		// The focus ring is an outline on the tile itself, so it isn't clipped.
		overflow: 'hidden',
		height: '2rem',
		borderRadius: token('radius.medium'),
		paddingBlock: token('space.075'),
		paddingInline: token('space.150'),
		font: token('font.body'),
		fontWeight: token('font.weight.medium'),
		whiteSpace: 'nowrap',
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.subtle'),
		transitionProperty: 'background-color, color',
		transitionDuration: token('motion.duration.short'),
		transitionTimingFunction: token('motion.easing.out.practical'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
		// Always rendered, transparent at rest, so the selected border fades in rather than
		// appearing in one frame.
		'&::after': {
			content: '""',
			position: 'absolute',
			inset: token('space.0'),
			borderRadius: 'inherit',
			borderStyle: 'solid',
			borderWidth: token('border.width'),
			borderColor: 'transparent',
			pointerEvents: 'none',
			transitionProperty: 'border-color',
			transitionDuration: token('motion.duration.short'),
			transitionTimingFunction: token('motion.easing.out.practical'),
		},
		'@media (prefers-reduced-motion: reduce)': {
			transitionDuration: '0ms',
			'&::after': {
				transitionDuration: '0ms',
			},
		},
	},
	tileSelected: {
		backgroundColor: token('color.background.selected'),
		color: token('color.text.selected'),
		'&:hover': {
			backgroundColor: token('color.background.selected.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.selected.pressed'),
		},
		'&::after': {
			borderColor: token('color.border.selected'),
		},
	},
	// On its way out: can't be clicked while it collapses.
	tileLeaving: {
		pointerEvents: 'none',
	},
});

// "All apps" has no logo, so its label is its only identifier and never collapses.
const AllAppsTile = ({
	isSelected,
	onSelect,
}: {
	isSelected: boolean;
	onSelect: () => void;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();

	return (
		<Pressable
			xcss={cx(styles.tile, isSelected && styles.tileSelected)}
			aria-pressed={isSelected}
			onClick={onSelect}
			testId="filter-all"
		>
			{formatMessage(messages.allApps)}
		</Pressable>
	);
};

const AppIcon = ({ option }: { option: AppOption }): React.JSX.Element => {
	// 3P glyph artwork has more internal padding than 1P logos, so needs a bigger box to match.
	const isCompactAtSize =
		isNounKey(option.value) ||
		is1pProductKey(option.value) ||
		(fg('rovo-search-team-eu-26-brand-icon-library') && isAtlassianOwned3pProduct(option.value));

	return (
		<ProductOrNounIcon
			label=""
			size={isCompactAtSize ? 'xsmall' : 'small'}
			productOrNounKey={option.value}
			isSelected={option.isSelected}
		/>
	);
};

// Always rendered, and collapsed by CSS when the tile isn't labelled. Removing it instead
// would leave a tile losing its label nothing to animate, so the label would vanish at once.
const AppLabel = ({
	option,
	isLabelled,
	textRef,
}: {
	option: AppOption;
	isLabelled: boolean;
	textRef: React.Ref<HTMLSpanElement>;
}): React.JSX.Element => (
	<Box as="span" xcss={cx(styles.labelTrack, isLabelled && styles.labelTrackExpanded)}>
		<Box as="span" ref={textRef} xcss={styles.labelText}>
			{option.label}
		</Box>
	</Box>
);

const AppControl = ({
	tile: { key, option, isEntering, isLeaving },
	isLabelled,
	onSelect,
	onLeft,
}: {
	tile: PresentTile;
	isLabelled: boolean;
	onSelect: OnSelectApp;
	onLeft: (key: string) => void;
}): React.JSX.Element => {
	const testId = `filter-${String(option.value)}`;
	const tileRef = useRef<HTMLButtonElement>(null);
	const labelTextRef = useRef<HTMLSpanElement>(null);
	const labelWidthRef = useRef<number | undefined>(undefined);
	const wasLeavingRef = useRef(false);

	// Only the first run matters: a tile that mounts mid-session grows out of zero width.
	const isEnteringOnMountRef = useRef(isEntering);
	useLayoutEffect(() => {
		const tileElement = tileRef.current;

		if (!isEnteringOnMountRef.current || !tileElement) {
			return;
		}

		animateTileIn(tileElement);

		// Strict Mode re-runs this effect on the same node, which would otherwise start a
		// second grow on top of the first.
		return () => cancelTileAnimations(tileElement);
	}, []);

	useLayoutEffect(() => {
		const tileElement = tileRef.current;

		if (!tileElement) {
			return;
		}

		if (isLeaving) {
			wasLeavingRef.current = true;
			let isCurrent = true;
			animateTileOut(tileElement).then(() => {
				if (isCurrent) {
					onLeft(key);
				}
			});

			// Same Strict Mode concern as the grow above, and it also releases the collapsed
			// hold when the app is reselected before the tile has gone.
			return () => {
				isCurrent = false;
				cancelTileAnimations(tileElement);
			};
		}

		// Reselected before its exit finished, so it grows back instead.
		if (wasLeavingRef.current) {
			wasLeavingRef.current = false;
			cancelTileAnimations(tileElement);
			animateTileIn(tileElement);
		}
	}, [isLeaving, key, onLeft]);

	// Only the promoted slot keeps its node while its app changes, since every other tile is
	// keyed by its app. Its label text swaps, which a transition can't see, so animate the
	// width between the two labels instead.
	useLayoutEffect(() => {
		const label = labelTextRef.current;

		if (!label) {
			return;
		}

		const width = measureLabelWidth(label);
		if (labelWidthRef.current !== undefined) {
			animateLabelWidth(label, labelWidthRef.current, width);
		}
		labelWidthRef.current = width;
	}, [option.value]);

	return (
		<Tooltip content={isLabelled || isLeaving ? undefined : option.label}>
			{(triggerProps: TriggerProps) => (
				<Pressable
					{...triggerProps}
					ref={mergeRefs<HTMLButtonElement>(triggerProps.ref, tileRef)}
					xcss={cx(
						styles.tile,
						option.isSelected && styles.tileSelected,
						isLeaving && styles.tileLeaving,
					)}
					aria-pressed={option.isSelected}
					aria-hidden={isLeaving || undefined}
					tabIndex={isLeaving ? -1 : undefined}
					onClick={(event) => {
						triggerProps.onClick(event);
						onSelect(option.value);
					}}
					onMouseEnter={
						isCodeSearchEnabled()
							? () => warmUpCodeSearchForHoveredProduct(String(option.value))
							: undefined
					}
					testId={isLeaving ? `${testId}-leaving` : testId}
				>
					<Inline alignBlock="center">
						<AppIcon option={option} />
						<AppLabel option={option} isLabelled={isLabelled} textRef={labelTextRef} />
					</Inline>
				</Pressable>
			)}
		</Tooltip>
	);
};

const AppsImpl = ({ maxVisibleApps }: { maxVisibleApps: number }): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [selectedProduct] = useSelectedProduct();
	const { visibleOptions, overflowOptions, areAllAppsLabelsShown, promotedValue } =
		useRefreshAppOptions({ maxVisibleApps });
	const handleSelect = useLiteFilterSelect({ allowDeselect: false });

	const tiles = useMemo(
		() =>
			visibleOptions.map((option) => ({
				key: option.value === promotedValue ? PROMOTED_SLOT_KEY : String(option.value),
				option,
			})),
		[visibleOptions, promotedValue],
	);
	const { presentTiles, onTileLeft } = useTilePresence(tiles, String(selectedProduct ?? ''));

	// Keeps `handleSelect`'s index argument the app's visible position, not counting a
	// tile that is only still on screen while it collapses.
	const visibleIndexByKey = useMemo(
		() => new Map(tiles.map(({ key }, index) => [key, index])),
		[tiles],
	);

	if (visibleOptions.length === 0 && overflowOptions.length === 0) {
		return null;
	}

	return (
		<Inline
			space="space.075"
			alignBlock="center"
			role="group"
			aria-label={formatMessage(messages.groupLabel)}
			testId="search-page-refresh-app-filters"
		>
			<AllAppsTile isSelected={!selectedProduct} onSelect={() => handleSelect(undefined, 0)} />
			{presentTiles.map((tile) => (
				<AppControl
					key={tile.key}
					tile={tile}
					// A leaving tile folds its label away as it collapses.
					isLabelled={
						!tile.isLeaving &&
						(tile.option.isSelected || (!selectedProduct && areAllAppsLabelsShown))
					}
					onLeft={onTileLeft}
					onSelect={(value) => handleSelect(value, (visibleIndexByKey.get(tile.key) ?? 0) + 1)}
				/>
			))}
		</Inline>
	);
};

type AppsProps = {
	maxVisibleApps?: number;
};

export const Apps = ({
	maxVisibleApps = DEFAULT_MAX_VISIBLE_APPS,
}: AppsProps): React.JSX.Element | null => {
	const [primaryProduct] = usePrimaryProduct();

	if (!shouldShowRefreshedFilterRow(primaryProduct)) {
		return null;
	}

	return <AppsImpl maxVisibleApps={maxVisibleApps} />;
};
